import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';

const TenantContext = createContext(null);

// Default branding values
const DEFAULT_BRANDING = {
  name: 'Golfshop.fit',
  logo_url: null,
  banner_image_url: null,
  primary_color: '#4A6741',
  accent_color: '#D4A55A',
  header_bg_color: '#FFFFFF',
  font_family: 'Chivo, sans-serif',
  // Homepage text fields
  hero_title: '',
  hero_subtitle: '',
  hero_cta_text: '',
  categories_title: '',
  categories_subtitle: '',
  features_title: '',
  features_subtitle: '',
  cta_title: '',
  cta_subtitle: '',
  // Header display customization
  hide_header_logo: false,
  hide_header_name: false,
  header_tagline: '',
  // Shipping
  in_store_pickup_enabled: false,
  // Subscription
  subscription_status: null,
  storefront_active: true
};

export function TenantProvider({ children, tenantSlug }) {
  const navigate = useNavigate();
  const [tenantState, setTenantState] = useState({
    isLoading: true,
    isValid: false,
    tenant: null,
    error: null,
    branding: DEFAULT_BRANDING
  });

  // Resolve tenant on mount or when slug changes
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function resolveTenant(attempt = 0) {
      if (!tenantSlug) {
        setTenantState({
          isLoading: false,
          isValid: false,
          tenant: null,
          error: 'No tenant specified',
          branding: DEFAULT_BRANDING
        });
        return;
      }

      try {
        const response = await api.get('/tenants/resolve', { signal: controller.signal });
        if (cancelled) return;
        const tenantData = response.data;
        
        // Extract branding from tenant data
        const branding = {
          name: tenantData.name || DEFAULT_BRANDING.name,
          logo_url: tenantData.logo_url || DEFAULT_BRANDING.logo_url,
          banner_image_url: tenantData.banner_image_url || DEFAULT_BRANDING.banner_image_url,
          primary_color: tenantData.primary_color || DEFAULT_BRANDING.primary_color,
          accent_color: tenantData.accent_color || DEFAULT_BRANDING.accent_color,
          header_bg_color: tenantData.header_bg_color || DEFAULT_BRANDING.header_bg_color,
          font_family: tenantData.font_family || DEFAULT_BRANDING.font_family,
          // Homepage text fields
          hero_title: tenantData.hero_title || DEFAULT_BRANDING.hero_title,
          hero_subtitle: tenantData.hero_subtitle || DEFAULT_BRANDING.hero_subtitle,
          hero_cta_text: tenantData.hero_cta_text || DEFAULT_BRANDING.hero_cta_text,
          categories_title: tenantData.categories_title || DEFAULT_BRANDING.categories_title,
          categories_subtitle: tenantData.categories_subtitle || DEFAULT_BRANDING.categories_subtitle,
          features_title: tenantData.features_title || DEFAULT_BRANDING.features_title,
          features_subtitle: tenantData.features_subtitle || DEFAULT_BRANDING.features_subtitle,
          cta_title: tenantData.cta_title || DEFAULT_BRANDING.cta_title,
          cta_subtitle: tenantData.cta_subtitle || DEFAULT_BRANDING.cta_subtitle,
          // Header display customization
          hide_header_logo: tenantData.hide_header_logo || false,
          hide_header_name: tenantData.hide_header_name || false,
          header_tagline: tenantData.header_tagline || '',
          // Shipping
          in_store_pickup_enabled: tenantData.in_store_pickup_enabled || false,
          // Subscription
          subscription_status: tenantData.subscription_status,
          storefront_active: tenantData.storefront_active !== false // Default to true for legacy tenants
        };
        
        setTenantState({
          isLoading: false,
          isValid: true,
          tenant: tenantData,
          error: null,
          branding
        });
        
        // Apply CSS variables for tenant branding
        applyBrandingCSS(branding);
      } catch (error) {
        if (cancelled || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return;

        const isNotFound = error.response?.status === 404;
        // A genuine 404 means the tenant truly doesn't exist - no point retrying,
        // show "Tenant not found" right away. Anything else (network hiccup,
        // timeout, transient error - e.g. from a cold direct load to a shared
        // product/category link with several requests firing at once) gets
        // retried a couple of times with backoff before giving up, so a brief
        // connection blip doesn't flash a scary "Store Not Found" page.
        if (!isNotFound && attempt < 2) {
          setTimeout(() => {
            if (!cancelled) resolveTenant(attempt + 1);
          }, 600 * (attempt + 1));
          return;
        }

        setTenantState({
          isLoading: false,
          isValid: false,
          tenant: null,
          error: isNotFound ? 'Tenant not found' : 'Failed to resolve tenant',
          branding: DEFAULT_BRANDING
        });
      }
    }

    setTenantState(prev => ({ ...prev, isLoading: true }));
    resolveTenant();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [tenantSlug]);

  // Function to apply branding CSS variables to the document
  const applyBrandingCSS = useCallback((branding) => {
    const root = document.documentElement;
    root.style.setProperty('--tenant-primary', branding.primary_color);
    root.style.setProperty('--tenant-accent', branding.accent_color);
    root.style.setProperty('--tenant-header-bg', branding.header_bg_color);
    root.style.setProperty('--tenant-font', branding.font_family);
  }, []);

  // Function to refresh branding (called after admin updates branding)
  const refreshBranding = useCallback(async () => {
    if (!tenantSlug) return;
    
    try {
      const response = await api.get('/tenants/resolve');
      const tenantData = response.data;
      
      const branding = {
        name: tenantData.name || DEFAULT_BRANDING.name,
        logo_url: tenantData.logo_url || DEFAULT_BRANDING.logo_url,
        banner_image_url: tenantData.banner_image_url || DEFAULT_BRANDING.banner_image_url,
        primary_color: tenantData.primary_color || DEFAULT_BRANDING.primary_color,
        accent_color: tenantData.accent_color || DEFAULT_BRANDING.accent_color,
        header_bg_color: tenantData.header_bg_color || DEFAULT_BRANDING.header_bg_color,
        font_family: tenantData.font_family || DEFAULT_BRANDING.font_family,
        // Homepage text fields
        hero_title: tenantData.hero_title || DEFAULT_BRANDING.hero_title,
        hero_subtitle: tenantData.hero_subtitle || DEFAULT_BRANDING.hero_subtitle,
        hero_cta_text: tenantData.hero_cta_text || DEFAULT_BRANDING.hero_cta_text,
        categories_title: tenantData.categories_title || DEFAULT_BRANDING.categories_title,
        categories_subtitle: tenantData.categories_subtitle || DEFAULT_BRANDING.categories_subtitle,
        features_title: tenantData.features_title || DEFAULT_BRANDING.features_title,
        features_subtitle: tenantData.features_subtitle || DEFAULT_BRANDING.features_subtitle,
        cta_title: tenantData.cta_title || DEFAULT_BRANDING.cta_title,
        cta_subtitle: tenantData.cta_subtitle || DEFAULT_BRANDING.cta_subtitle,
        // Header display customization
        hide_header_logo: tenantData.hide_header_logo || false,
        hide_header_name: tenantData.hide_header_name || false,
        header_tagline: tenantData.header_tagline || '',
        // Shipping
        in_store_pickup_enabled: tenantData.in_store_pickup_enabled || false,
        // Subscription
        subscription_status: tenantData.subscription_status,
        storefront_active: tenantData.storefront_active !== false
      };
      
      setTenantState(prev => ({
        ...prev,
        tenant: tenantData,
        branding
      }));
      
      applyBrandingCSS(branding);
    } catch (error) {
      console.error('Failed to refresh branding:', error);
    }
  }, [tenantSlug, applyBrandingCSS]);

  const value = useMemo(() => ({
    tenantSlug: tenantSlug || null,
    tenant: tenantState.tenant,
    isLoading: tenantState.isLoading,
    isValid: tenantState.isValid,
    error: tenantState.error,
    branding: tenantState.branding,
    refreshBranding,
    // Helper to create tenant-prefixed paths
    tenantPath: (path) => {
      if (!tenantSlug) return path;
      // Ensure path starts with /
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      return `/${tenantSlug}${normalizedPath}`;
    },
    // Navigate within tenant context
    tenantNavigate: (path, options) => {
      if (!tenantSlug) {
        navigate(path, options);
        return;
      }
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      navigate(`/${tenantSlug}${normalizedPath}`, options);
    },
    // Check if currently in a tenant context
    hasTenant: !!tenantSlug,
  }), [tenantSlug, tenantState, navigate, refreshBranding]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    // Return a default context for components outside TenantProvider
    return {
      tenantSlug: null,
      tenant: null,
      isLoading: false,
      isValid: false,
      error: null,
      branding: DEFAULT_BRANDING,
      refreshBranding: () => {},
      tenantPath: (path) => path,
      tenantNavigate: () => {},
      hasTenant: false,
    };
  }
  return context;
}

export default TenantContext;
